import { ChangeDetectorRef, Component, ElementRef, OnDestroy, OnInit, ViewChild } from "@angular/core";
import { ChecklistsService } from "../../../../services/checklists.service";
import { IChecklistItem, IChecklistItemRule, IDocumentType } from "../../../../models/settings.model";
import { CardModule } from "primeng/card";
import { RadioButtonModule } from "primeng/radiobutton";
import { CommonModule } from "@angular/common";
import { ButtonModule } from "primeng/button";
import { Textarea } from "primeng/textarea";
import { FormsModule } from "@angular/forms";
import { PaginatorModule, PaginatorState } from "primeng/paginator";
import { SkeletonModule } from "primeng/skeleton";
import { SkeletonService } from "../../../../services/skeleton.service";
import { RiskMatrixComponent } from "../../../risk-matrix/risk-matrix.component";
import { DocumentTypeService } from "../../../../services/document-type.service";
import { TooltipModule } from "primeng/tooltip";
import { ToastService } from "../../../../../@core/services/toast.service";
import { ButtonComponent } from "../../../../../@core/components/ui/button/button.component";
import { DialogComponent } from "../../../../../@core/components/ui/dialog/dialog.component";
import { DialogService } from "../../../../../@core/services/dialog.service";

const IMPACT_COLOR = {
    1: '#1EB768',
    2: '#B2D34A',
    3: '#F7A232',
    4: '#E86829',
    5: '#DC3622'
}

@Component({
    selector: 'app-risks',
    imports: [
        CardModule,
        RadioButtonModule,
        CommonModule,
        ButtonModule,
        Textarea,
        FormsModule,
        PaginatorModule,
        SkeletonModule,
        RiskMatrixComponent,
        TooltipModule,
        ButtonComponent,
        DialogComponent,
    ],
    standalone: true,
    templateUrl: './risks.component.html',
    styleUrl: './risks.component.scss'
})
export class RisksComponent implements OnInit, OnDestroy {

    checklists: Partial<IChecklistItem>[] = [];
    impactRange: number = 5;
    defaultImpact: number[] = Array.from({ length: this.impactRange }, (_, i) => i + 1);
    defaultProbability: number[] = Array.from({ length: this.impactRange }, (_, i) => i + 1);

    impactByChecklist: { [key: string]: IChecklistItemRule } = {}
    IMPACT_COLOR: { [key: number]: string } = IMPACT_COLOR;

    rulesDialog: { visible: boolean, checklistId: string | null, rules: string[] } = { visible: false, checklistId: null, rules: [] };
    riskMatrixDialog: boolean = false;

    //paginator
    loading: boolean = false;
    skeletonCount: number = 1;
    defaultPaginatorValues = {
        first: 0,
        rows: 20
    }
    first: number = this.defaultPaginatorValues.first;
    rows: number = this.defaultPaginatorValues.rows;
    totalRecords: number = 0;

    docTypeLoading: boolean = false
    documentTypes: IDocumentType[] = [];
    selectedDocType!: string;

    constructor(
        private checklistService: ChecklistsService,
        private cdr: ChangeDetectorRef,
        private skeletonService: SkeletonService,
        private toastService: ToastService,
        private documentTypeService: DocumentTypeService,
        private dialogService: DialogService
    ) {

    }

    ngOnInit(): void {
        this.getDocumentTypes()
        this.setupResizeObserver();
    }

    ngOnDestroy() {
        this.cleanupResizeObserver();
    }

    //#region skeleton operations
    private setupResizeObserver() {
        this.skeletonService.setupResizeObserver(() => {
            this.calculateSkeletonCount();
            this.cdr.detectChanges();
        });
    }

    private cleanupResizeObserver() {
        this.skeletonService.cleanupResizeObserver();
    }

    calculateSkeletonCount() {
        const itemHeight = 15 * 16; // 15rem converted to pixels (assuming 1rem = 16px)
        const gap = 4 * 16; // 4rem gap converted to pixels (matching the gap-4 class)
        const padding = 2 * 16; // 2rem padding (1rem top + 1rem bottom)

        this.skeletonCount = this.skeletonService.calculateSkeletonCount(itemHeight, gap, padding);
    }

    getSkeletonCount(): number[] {
        return this.skeletonService.getSkeletonArray(this.skeletonCount);
    }
    //#endregion

    getDocumentTypes() {
        this.docTypeLoading = true
        this.documentTypeService.getDocTypes().subscribe(res => {
            if (res && res.success) {
                this.docTypeLoading = false
                this.documentTypes = res.data || [];
                if (this.documentTypes.length > 0) {
                    this.changeDocumentType(this.selectedDocType ?? this.documentTypes[0]._id);
                }
            }
            else
                this.docTypeLoading = false
        })
    }

    changeDocumentType(documentType: string) {
        this.selectedDocType = documentType;
        this.onPageChange({});
    }

    onPageChange(event: PaginatorState) {
        this.first = event.first ?? this.defaultPaginatorValues.first;
        this.rows = event.rows ?? this.defaultPaginatorValues.rows;
        const skip = this.first;
        this.loading = true;
        this.getChecklists(skip, this.rows)
    }

    getChecklists(skip = this.defaultPaginatorValues.first, limit = this.defaultPaginatorValues.rows) {
        this.checklistService.getChecklists({ skip, limit, documentType: this.selectedDocType, fetchRisks: true }).subscribe((res) => {
            if (res && res.success) {
                this.checklists = res.data.checklists;
                this.impactByChecklist = res.data.risks;
                this.totalRecords = res.data.total ?? 0;
                this.loading = false;
            } else {
                this.loading = false;
            }
        });
    }

    addRules(checklistId: string) {
        this.rulesDialog = { visible: true, checklistId, rules: Array.from({ length: this.impactRange }, (_, i) => '') };
    }

    editRules(checklistId: string) {
        const existingRules = this.impactByChecklist[checklistId]?.impactRules || [];
        this.rulesDialog = { visible: true, checklistId, rules: Array.from({ length: this.impactRange }, (_, i) => existingRules[i]?.rule) };
    }

    updateRule(index: number, event: any) {
        this.rulesDialog.rules[index] = event?.target.value;
    }

    saveRules() {
        const checklistId = this.rulesDialog.checklistId;
        if (checklistId) {
            const payload: Partial<IChecklistItemRule> = {
                checklist: checklistId as string,
                impactRules: this.rulesDialog.rules.map((curr, index) => ({
                    rule: curr,
                    impact: index + 1
                }))
            }
            this.addRulesToChecklist(payload)
        }
    }

    addRulesToChecklist(payload: Partial<IChecklistItemRule>) {
        this.checklistService.addRulesToChecklist(payload).subscribe(res => {
            if (res && res.success) {
                this.impactByChecklist[payload.checklist as string] = res.data;
                this.rulesDialog = { visible: false, checklistId: null, rules: [] }
                this.toastService.success('Successfully updated the selected checklist.')
            } else {
                this.rulesDialog = { visible: false, checklistId: null, rules: [] }
            }
        })
    }

    removeRules(checklistId: string) {
        this.dialogService.confirm({
            title: 'Are you sure?',
            description: 'This action will permanently delete all the rules',
            confirmText: 'Yes',
            cancelText: 'No',
        }).subscribe(confirmed => {
            if (confirmed) {
                this.checklistService.deleteRulesLinkedToChecklist(this.impactByChecklist[checklistId]?._id).subscribe(res => {
                    if (res && res.success) {
                        this.impactByChecklist[checklistId] = res.data;
                        this.toastService.success('Successfully removed the rules for the selected checklist.');
                    } else {
                        this.toastService.error('Failed to remove rules for the selected checklist.');
                    }
                });
            }
        });
    }

    updateImpact(checklistId: string) {
        if (checklistId) {
            const payload: Partial<IChecklistItemRule> = {
                checklist: checklistId,
                impact: this.impactByChecklist[checklistId]?.impact
            }
            this.addRulesToChecklist(payload)
        }
    }

    updateProbability(checklistId: string) {
        if (checklistId) {
            const payload: Partial<IChecklistItemRule> = {
                checklist: checklistId,
                probability: this.impactByChecklist[checklistId]?.probability
            }
            this.addRulesToChecklist(payload)
        }
    }

    onShowDialog() {
        this.riskMatrixDialog = true
    }

}
